/**
 * Minha senha: trocar a própria senha. Com senha provisória (criada por um
 * administrador), esta é a única tela até a pessoa criar a dela.
 * Trocar a senha encerra as sessões abertas em outros aparelhos.
 *
 * Lê: api.trocarMinhaSenha.
 * Usado por: App.tsx (rota #/conta e o primeiro acesso com senha provisória).
 */
import { useState, type FormEvent } from 'react';
import { Aviso, ErroNaTela } from '../componentes/basicos.tsx';
import { api, ErroApi } from '../dados/api.ts';
import type { Usuario } from '../dominio/tipos.ts';

interface Props {
  usuario: Usuario;
  aoTrocar: (usuario: Usuario) => void;
}

export function Conta({ usuario, aoTrocar }: Props) {
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [repetir, setRepetir] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<ErroApi | null>(null);
  const [pronto, setPronto] = useState(false);
  const diferentes = repetir !== '' && nova !== repetir;

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (nova !== repetir) return;
    setEnviando(true);
    setErro(null);
    try {
      const resposta = await api.trocarMinhaSenha(atual, nova);
      setAtual('');
      setNova('');
      setRepetir('');
      setPronto(true);
      aoTrocar(resposta.usuario);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="pagina">
      <div className="pagina-titulo">
        <div>
          <h1>{usuario.senhaProvisoria ? 'Crie a sua senha' : 'Minha senha'}</h1>
          <p>
            {usuario.nome} · {usuario.email}
          </p>
        </div>
      </div>
      <form className="cartao pilha formulario-estreito" onSubmit={enviar} method="post">
        {usuario.senhaProvisoria ? (
          <Aviso tipo="info">Você entrou com uma senha provisória. Crie a sua para continuar (só você vai saber).</Aviso>
        ) : null}
        {pronto ? <Aviso tipo="ok">Senha trocada. Nos outros aparelhos, entre de novo com a senha nova.</Aviso> : null}
        <input type="email" autoComplete="username" value={usuario.email} readOnly hidden />
        <label>
          {usuario.senhaProvisoria ? 'Senha provisória (a que você recebeu)' : 'Senha atual'}
          <input type="password" autoComplete="current-password" required value={atual} onChange={(e) => setAtual(e.target.value)} />
        </label>
        <label>
          Senha nova (mínimo 10 caracteres)
          <input type="password" autoComplete="new-password" required minLength={10} value={nova} onChange={(e) => setNova(e.target.value)} />
        </label>
        <label>
          Repita a senha nova
          <input type="password" autoComplete="new-password" required value={repetir} onChange={(e) => setRepetir(e.target.value)} />
        </label>
        {diferentes ? <p className="mensagem-erro">As senhas novas não são iguais.</p> : null}
        {erro ? <ErroNaTela erro={erro} /> : null}
        <button type="submit" className="botao" disabled={enviando || diferentes}>
          {enviando ? 'Salvando…' : 'Salvar senha'}
        </button>
      </form>
    </div>
  );
}
